// Decyzon — predictionSubject.
//
// When a contract's source options are literally Yes/No, "Lean: Yes" alone is
// useless in a scrolling feed. This helper extracts a short subject from the
// market question so the UI can render "Lean: Yes — Sunderland wins".
//
// Pure, deterministic. No invention. Returns null when the question can't be
// parsed cleanly — caller must NOT fabricate a fallback.
//
// Examples (Yes pick):
//   "Will Sunderland AFC win on 2026-04-24?"             → "Sunderland AFC wins"
//   "Will the Democratic Party win the MI-11 House seat?" → "Democrats win MI-11"
//   "Will BTC reach $100k by 2026?"                       → "BTC reaches $100k"
//   "Starmer out by June 30, 2026?"                       → "Starmer out"
//   "Reya FDV above $300M one day after launch?"         → "Reya FDV above $300M"
//   "Cleveland vs Toronto Winner?"                        → null (use market top instead)
//
// Examples (No pick → buildYesNoSuffix returns negated form):
//   "Will Tesla report >500k deliveries in Q2 2026?" / No → "Tesla does not report >500k deliveries"
//   "Starmer out by June 30, 2026?"                  / No → "Starmer not out"
//   "Will it snow in NYC on Dec 25?"                 / No → "no snow in NYC"

const LEAD_RX = /^(will|does|do|is|are|has|have|can|did)\s+/i;
const TRAIL_DATE_RX = /\s+(?:on|by|before|after|until|in|during)\s+(?:\d{4}-\d{2}-\d{2}|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{1,2}(?:,?\s*\d{4})?|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{4})\??\s*$/i;
// Trailing time clauses without month names — "by June 30, 2026", "in Q2 2026",
// "one day after launch", "before Jan 1".
const TRAIL_TIME_RX = /\s+(?:by|before|after|until|in|during|on)\s+(?:q[1-4]\s*\d{4}|\d{4}|end of [a-z]+|[a-z]+ \d{4})$/i;
const TRAIL_LAUNCH_RX = /\s+one\s+day\s+after\s+launch$/i;
const PAREN_OUTER_RX = /\s*[\(\[][^)\]]+[\)\]]\s*$/;
// Mid-string parentheticals: "(HIGH)", "(CL)", "[NYSE]" — strip entirely.
const PAREN_INNER_RX = /\s*[\(\[][^)\]]*[\)\]]/g;

const VERB_THIRD_PERSON: Record<string, string> = {
  win: "wins",
  reach: "reaches",
  hit: "hits",
  cross: "crosses",
  beat: "beats",
  pass: "passes",
  exceed: "exceeds",
  happen: "happens",
  occur: "occurs",
  snow: "snows",
  rain: "rains",
  resign: "resigns",
  launch: "launches",
  release: "releases",
  drop: "drops",
  rise: "rises",
  fall: "falls",
  break: "breaks",
  visit: "visits",
  report: "reports",
  announce: "announces",
  sign: "signs",
  agree: "agrees",
  end: "ends",
  start: "starts",
  meet: "meets",
};
// Reverse map: 3rd-person → base form, for "does not <base>" negation.
const BASE_FROM_THIRD: Record<string, string> = Object.entries(VERB_THIRD_PERSON)
  .reduce((acc, [base, third]) => { acc[third] = base; return acc; }, {} as Record<string, string>);
// Special negation rewrites that read better than "does not <verb>".
const NO_REWRITE: Record<string, string> = {
  snows: "no snow",
  rains: "no rain",
};

// Words ending in 's' that are still SINGULAR proper nouns — must NOT be
// treated as plural for verb agreement.
const SINGULAR_S_ALLOWLIST = new Set([
  "mercedes", "williams", "miss", "boss", "bus", "yes", "kansas", "texas",
  "arkansas", "paris", "athens", "athletics", "physics", "news",
]);
// Known plural team / party tokens — force base verb form.
const PLURAL_FORCED = new Set([
  "democrats", "republicans", "mets", "yankees", "red sox", "white sox",
  "celtics", "lakers", "warriors", "knicks", "nets", "76ers", "sixers",
  "bulls", "rockets", "mavs", "mavericks", "suns", "kings", "spurs",
  "raptors", "bucks", "nuggets", "wizards", "jazz",
]);

function conjugate(verb: string, plural: boolean): string {
  const lower = verb.toLowerCase();
  if (plural) return BASE_FROM_THIRD[lower] ?? lower.replace(/s$/, "");
  return VERB_THIRD_PERSON[lower] ?? (lower.endsWith("s") ? lower : `${lower}s`);
}

function shortenParty(s: string): string {
  return s
    .replace(/\bthe\s+democratic\s+party\b/gi, "Democrats")
    .replace(/\bthe\s+republican\s+party\b/gi, "Republicans")
    .replace(/\bdemocratic\s+party\b/gi, "Democrats")
    .replace(/\brepublican\s+party\b/gi, "Republicans");
}

function dropArticles(s: string): string {
  return s.replace(/^(the|a|an)\s+/i, "");
}

function clip(s: string, max: number): string {
  if (s.length <= max) return s;
  const cut = s.lastIndexOf(" ", max - 1);
  const at = cut > max * 0.6 ? cut : max - 1;
  return s.slice(0, at).replace(/[\s.,;:—-]+$/, "") + "…";
}

function stripTails(t: string): string {
  let out = t.replace(/\?+$/, "").trim();
  out = out.replace(PAREN_OUTER_RX, "").trim();
  out = out.replace(TRAIL_LAUNCH_RX, "").trim();
  out = out.replace(TRAIL_DATE_RX, "").trim();
  out = out.replace(TRAIL_TIME_RX, "").trim();
  out = out.replace(/,\s*\d{4}$/, "").trim();
  // Repeat date-strip in case multiple trailing clauses stack.
  out = out.replace(TRAIL_DATE_RX, "").trim();
  out = out.replace(TRAIL_TIME_RX, "").trim();
  // Strip mid parentheticals last so "Crude Oil (CL) hit ..." → "Crude Oil hit ...".
  out = out.replace(PAREN_INNER_RX, " ").replace(/\s+/g, " ").trim();
  return out;
}

function looksPlural(subjectTokens: string): boolean {
  const lower = subjectTokens.trim().toLowerCase();
  if (PLURAL_FORCED.has(lower)) return true;
  // Last word ends in 's' and isn't on the singular allowlist.
  const last = lower.split(/\s+/).pop() ?? "";
  if (!last.endsWith("s")) return false;
  if (SINGULAR_S_ALLOWLIST.has(last)) return false;
  // "AFC", "USA", short acronyms — not plural.
  if (last.length <= 3) return false;
  return true;
}

/** Recognised "headline" patterns (no leading verb). Returns the cleaned
 *  subject + optional verb info for negation, or null. */
function parseHeadline(t: string): { subject: string; verb: string | null } | null {
  // "<A> vs <B> Winner" / "Winner?" — ambiguous without market data; defer.
  if (/\bvs\.?\b.*\bwinner\b/i.test(t)) return null;

  // "<X> out" / "<X> out by ..."
  const outM = t.match(/^(.+?)\s+out$/i);
  if (outM) return { subject: `${outM[1].trim()} out`, verb: null };

  // "<X> FDV above $Y" / "<X> above $Y"
  const aboveM = t.match(/^(.+?)\s+(above|below|under|over)\s+(.+)$/i);
  if (aboveM) return { subject: `${aboveM[1].trim()} ${aboveM[2].toLowerCase()} ${aboveM[3].trim()}`, verb: null };

  // "<X> agrees to <Y>" — explicit verb in headline.
  const agreesM = t.match(/^(.+?)\s+agrees\s+to\s+(.+)$/i);
  if (agreesM) return { subject: `${agreesM[1].trim()} agrees to ${agreesM[2].trim()}`, verb: "agrees" };

  // Short headline (≤8 tokens) with no verb — accept as-is.
  const tokens = t.split(/\s+/);
  if (tokens.length > 0 && tokens.length <= 8) {
    return { subject: t, verb: null };
  }
  return null;
}

interface SubjectParts {
  /** Affirmative form: "Tesla reports >500k deliveries" / "Starmer out". */
  affirmative: string;
  /** Negation form: "Tesla does not report >500k deliveries" / "Starmer not out". */
  negation: string;
}

function buildSubjectParts(q: string): SubjectParts | null {
  if (!q || typeof q !== "string") return null;
  const cleaned = stripTails(q.trim());
  if (!cleaned) return null;

  const m = cleaned.match(LEAD_RX);
  if (!m) {
    const head = parseHeadline(cleaned);
    if (!head) return null;
    let subj = shortenParty(head.subject);
    subj = subj.replace(/\s+/g, " ").trim();
    subj = dropArticles(subj);
    if (!subj) return null;
    subj = subj.charAt(0).toUpperCase() + subj.slice(1);
    const affirmative = clip(subj, 48);
    // Negation for headlines: prefix "not" before the descriptor word.
    let negation: string;
    if (head.verb && BASE_FROM_THIRD[head.verb.toLowerCase()]) {
      const base = BASE_FROM_THIRD[head.verb.toLowerCase()];
      negation = affirmative.replace(new RegExp(`\\b${head.verb}\\b`, "i"), `does not ${base}`);
    } else {
      // Insert "not" before the descriptor word (above/below/over/under/out/
      // in/at). For "<X> vs <Y> end in a draw" insert before "end".
      // Fall back to prefixing the whole subject.
      const descriptorRx = /\b(above|below|over|under|out|in|at|by|on|before|after|to)\b/i;
      const dm = affirmative.match(descriptorRx);
      if (dm && dm.index != null && dm.index > 0) {
        negation = `${affirmative.slice(0, dm.index).trimEnd()} not ${affirmative.slice(dm.index)}`;
      } else {
        negation = affirmative.toLowerCase().startsWith("not ") ? affirmative : `not ${affirmative}`;
      }
    }
    return { affirmative, negation: clip(negation, 64) };
  }

  const rest = cleaned.slice(m[0].length).trim();
  if (!rest) return null;
  const tokens = rest.split(/\s+/);

  // Pronoun lead: "it snows in NYC" → affirmative "It snows in NYC", negation "no snow in NYC".
  if (/^it$/i.test(tokens[0])) {
    const verbTok = tokens[1]?.replace(/[^a-zA-Z]/g, "").toLowerCase() ?? "";
    const tail = tokens.slice(2).join(" ").trim();
    if (verbTok && VERB_THIRD_PERSON[verbTok]) {
      const third = VERB_THIRD_PERSON[verbTok];
      const affirmative = clip(`It ${third}${tail ? " " + tail : ""}`, 48);
      const negPhrase = NO_REWRITE[third] ?? `does not ${verbTok}`;
      const negation = clip(`${negPhrase}${tail ? " " + tail : ""}`, 64);
      return { affirmative, negation };
    }
  }

  // Sports draw pattern: "Will A vs B end in a draw?".
  const drawM = rest.match(/^(.+?)\s+end\s+in\s+a\s+draw$/i);
  if (drawM) {
    return {
      affirmative: clip(`${drawM[1].trim()} ends in a draw`, 56),
      negation: clip(`${drawM[1].trim()} does not end in a draw`, 72),
    };
  }

  // Find verb in first 8 tokens (longer subject names need more headroom).
  let verbIdx = -1;
  const limit = Math.min(tokens.length, 8);
  for (let i = 1; i < limit; i++) {
    const lower = tokens[i].toLowerCase().replace(/[^a-z]/g, "");
    if (VERB_THIRD_PERSON[lower]) { verbIdx = i; break; }
  }

  if (verbIdx > 0) {
    const subjectTokens = shortenParty(tokens.slice(0, verbIdx).join(" "));
    const verbBase = tokens[verbIdx].replace(/[^a-zA-Z]/g, "").toLowerCase();
    const plural = looksPlural(subjectTokens);
    const verb = conjugate(verbBase, plural);
    const tail = tokens.slice(verbIdx + 1).join(" ").trim();
    const tailClean = tail
      .replace(/^the\s+/i, "")
      .replace(/\s+(house\s+seat|senate\s+seat|election|race)\b.*$/i, "")
      .trim();
    let subj = [subjectTokens, verb, tailClean].filter(Boolean).join(" ");
    subj = subj.replace(/\s+/g, " ").trim();
    subj = dropArticles(subj);
    if (!subj) return null;
    subj = subj.charAt(0).toUpperCase() + subj.slice(1);
    const affirmative = clip(subj, 48);
    // Negation: "<subject> does not <baseVerb> <tail>".
    const negSubject = (subjectTokens.charAt(0).toUpperCase() + subjectTokens.slice(1)).trim();
    const negation = clip(
      [negSubject, plural ? "do not" : "does not", verbBase, tailClean].filter(Boolean).join(" "),
      72,
    );
    return { affirmative, negation };
  }

  // Copular fallback ("Is X Y?", "Has X Y?") — no verb to conjugate.
  let subj = shortenParty(rest);
  subj = subj.replace(/\s+/g, " ").trim();
  subj = dropArticles(subj);
  if (!subj) return null;
  subj = subj.charAt(0).toUpperCase() + subj.slice(1);
  const affirmative = clip(subj, 48);
  const parts = affirmative.split(" ");
  const negation = parts.length >= 2
    ? clip(`${parts[0]} not ${parts.slice(1).join(" ")}`, 64)
    : `not ${affirmative.toLowerCase()}`;
  return { affirmative, negation };
}

/** Extract a short affirmative subject phrase from a Yes/No question.
 *  Returns null if the question can't be parsed cleanly. */
export function subjectFromQuestion(q: string | null | undefined): string | null {
  const parts = buildSubjectParts(q ?? "");
  return parts?.affirmative ?? null;
}

/** Decide if the visible pick label is the literal word "yes" or "no". */
export function isYesNoLabel(label: string | null | undefined): boolean {
  if (!label) return false;
  const t = label.replace(/\s+/g, " ").trim().toLowerCase();
  return t === "yes" || t === "no";
}

/** Build the suffix shown after a Yes/No pick. Returns null when no clean
 *  subject can be derived — caller must omit the suffix in that case. */
export function buildYesNoSuffix(
  label: string | null | undefined,
  question: string | null | undefined,
): string | null {
  if (!isYesNoLabel(label)) return null;
  const parts = buildSubjectParts(question ?? "");
  if (!parts) return null;
  return label!.toLowerCase() === "no" ? parts.negation : parts.affirmative;
}
